import { LumenButton, LumenIcon } from '@photoshelter/socialie-components';
import Form from 'components/Form';
import PhotoShelterGallerySelect from 'components/PhotoShelterGallerySelect';
import useCurrentAccount from 'hooks/useCurrentAccount';
import useTranslateErrors from 'hooks/useTranslateErrors';
import { IconThumbsDownFilled, IconThumbsUpFilled } from 'icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Destination from 'types/Destination';
import RoutingRule from 'types/RoutingRule';
import UgcRequestErrorPayload, {
  RoutingRuleErrorPayload,
} from 'types/UgcRequestErrorPayload';
import UgcRequestParams from 'types/UgcRequestParams';

interface Props {
  value: UgcRequestParams['routingRules'];
  onChange: (value: UgcRequestParams['routingRules']) => void;
  errors?: UgcRequestErrorPayload | null;
}

const destinationIcons = {
  photo_shelter: 'photoshelter-color',
  socialie: 'socialie-color',
} as const;

export default function RoutingRules(props: Props) {
  const { value, onChange, errors } = props;
  const account = useCurrentAccount();
  const { t } = useTranslation();

  const { rulesByDestinationId, ruleErrorsByDestinationId } = useMemo(() => {
    const rulesByDestinationId: Record<Destination['id'], RoutingRule[]> = {};
    const ruleErrorsByDestinationId: Record<
      Destination['id'],
      RoutingRuleErrorPayload[]
    > = {};
    value.forEach((rule, idx) => {
      if (!rulesByDestinationId[rule.destinationId]) {
        rulesByDestinationId[rule.destinationId] = [];
      }
      rulesByDestinationId[rule.destinationId].push(rule);
      const ruleError = errors?.routingRules?.[idx];
      if (ruleError) {
        if (!ruleErrorsByDestinationId[rule.destinationId]) {
          ruleErrorsByDestinationId[rule.destinationId] = [];
        }
        ruleErrorsByDestinationId[rule.destinationId].push(ruleError);
      }
    });
    return {
      rulesByDestinationId: rulesByDestinationId,
      ruleErrorsByDestinationId: ruleErrorsByDestinationId,
    };
  }, [value, errors?.routingRules]);

  if (!account?.destinations?.length) return null;

  const handleAddRule = (destinationId: Destination['id']) => {
    onChange([
      ...value,
      {
        destinationId,
        event: 'created',
        id: `_NEW_${Date.now()}`,
        path: null,
        displayPath: null,
        label: '',
      },
    ]);
  };

  const handleRemoveRule = (ruleId: RoutingRule['id']) => {
    onChange([
      ...value.map((rule) =>
        rule.id === ruleId ? { ...rule, _destroy: true } : rule
      ),
    ]);
  };

  const handleUpdateRule = (
    ruleId: RoutingRule['id'],
    updates: Partial<RoutingRule>
  ) => {
    onChange([
      ...value.map((rule) =>
        rule.id === ruleId ? { ...rule, ...updates } : rule
      ),
    ]);
  };

  return (
    <Form.Section
      title={t('ugcRequestBuilder.form.routingRules.title')}
      description={t('ugcRequestBuilder.form.dataCapture.description')}
    >
      {account.destinations.map((destination) => (
        <div
          className="border-default mb-3 rounded-lg p-2 last:mb-0"
          key={destination.id}
        >
          <div className="border-b-default mb-2 flex items-center gap-1.5 pb-2">
            <div className="h-3 w-3 shrink-0">
              <LumenIcon
                id={destinationIcons[destination.destinationType]}
                className="h-3 w-3"
              />
            </div>
            <p className="leading-none">
              <span className="text-dark">{destination.config.label}</span>
              <br />
              <span className="text-12 text-light">
                {t(
                  `ugcRequestBuilder.form.routingRules.destinations.${destination.destinationType}`
                )}
              </span>
            </p>
          </div>

          {rulesByDestinationId[destination.id]?.map((rule, idx) =>
            rule._destroy ? (
              <></>
            ) : (
              <div
                className="border-default mb-2 rounded-lg bg-grey8 p-1 last:mb-3"
                key={rule.id}
              >
                <div className="mb-0.5 flex justify-end">
                  <LumenButton
                    color="transparent"
                    size="xs"
                    leftIcon="close"
                    aria-label={t(
                      'ugcRequestBuilder.form.routingRules.removeRule'
                    )}
                    onClick={() => handleRemoveRule(rule.id)}
                  />
                </div>
                <div className="p-1 pt-0">
                  <Form.Field
                    label={t('ugcRequestBuilder.form.routingRules.eventLabel')}
                  >
                    <Form.ToggleGroup variant="white">
                      <Form.ToggleGroup.Option
                        isSelected={rule.event === 'created'}
                        onSelect={() =>
                          handleUpdateRule(rule.id, { event: 'created' })
                        }
                        title={t(
                          'ugcRequestBuilder.form.routingRules.eventCreated'
                        )}
                      />

                      <Form.ToggleGroup.Option
                        isSelected={rule.event === 'starred'}
                        onSelect={() =>
                          handleUpdateRule(rule.id, { event: 'starred' })
                        }
                        icon={IconThumbsUpFilled}
                        title={t(
                          'ugcRequestBuilder.form.routingRules.eventStarred'
                        )}
                      />

                      <Form.ToggleGroup.Option
                        isSelected={rule.event === 'rejected'}
                        onSelect={() =>
                          handleUpdateRule(rule.id, { event: 'rejected' })
                        }
                        icon={IconThumbsDownFilled}
                        title={t(
                          'ugcRequestBuilder.form.routingRules.eventRejected'
                        )}
                      />
                    </Form.ToggleGroup>
                  </Form.Field>

                  {destination.destinationType === 'photo_shelter' ? (
                    <PhotoShelterFields
                      rule={rule}
                      onUpdate={(changes) => handleUpdateRule(rule.id, changes)}
                      errors={
                        ruleErrorsByDestinationId?.[destination.id]?.[idx]
                      }
                    />
                  ) : destination.destinationType === 'socialie' ? (
                    <SocialieFields
                      rule={rule}
                      onUpdate={(changes) => handleUpdateRule(rule.id, changes)}
                    />
                  ) : null}
                </div>
              </div>
            )
          )}
          <button
            onClick={() => handleAddRule(destination.id)}
            className="text-14 font-semibold text-brandPrimary underline disabled:opacity-50"
            disabled={
              rulesByDestinationId[destination.id]?.filter(
                (rule) => !rule._destroy
              ).length >= 1
            }
          >
            + {t('ugcRequestBuilder.form.routingRules.addButton')}
          </button>
        </div>
      ))}
    </Form.Section>
  );
}

interface FieldComponentProps {
  rule: RoutingRule;
  onUpdate: (changes: Partial<RoutingRule>) => void;
  errors?: RoutingRuleErrorPayload | null;
}

const PhotoShelterFields = (props: FieldComponentProps) => {
  const { rule, onUpdate, errors } = props;
  const { t } = useTranslation();
  const translateErrors = useTranslateErrors();

  return (
    <PhotoShelterGallerySelect
      value={{ id: rule.path ?? '', label: rule.displayPath ?? '' }}
      label={t('ugcRequestBuilder.form.routingRules.photo_shelter.pathLabel')}
      onChange={({ id, label }) => {
        onUpdate({ path: id, displayPath: label });
      }}
      destinationId={rule.destinationId}
      errors={translateErrors(errors?.path)}
    />
  );
};

// No extra fields for Socialie
const SocialieFields = (props: FieldComponentProps) => {
  return null;
};
